/*
 * Copyright 2025 coze-dev Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { useEffect, useRef, useState, type ReactNode } from 'react';

import classNames from 'classnames';
import { Spin } from '@coze-arch/coze-design';

import { startCozeAbrChatEmbed } from '../coze/start-coze-abr-chat-embed';
import { ensureCozeUcSession } from '../coze/ensure-coze-uc-session';
import {
  buildCozeAiQueryLauncher,
  getCozeEmbedEnv,
  isAbrWebchatDirectEmbedEnabled,
  validateAiQueryOidcConfig,
  type CozeChatQuery,
} from '../ai-query-oidc';

import styles from './abr-chat-frame.module.less';

export type AbrChatInferenceMode =
  | 'llm'
  | 'ontology'
  | 'document_workspace'
  | 'starrocks'
  | 'ontology_starrocks';

export interface AbrChatFrameProps {
  botId: string;
  spaceId: string;
  /** 当前推理模式；会话配置保存由 switchInferenceConfig 完成 */
  inferenceMode?: AbrChatInferenceMode;
  /** 编辑器已有的机器人名称，写入 iframe 地址供预览先出标题 */
  botName?: string;
  /** 文档集 id；变更时重载 iframe，避免沿用旧文档目录会话 */
  workspaceId?: string;
  /**
   * 模式/文档集危险切换：要求 abr-webchat 归档旧会话后再进空白态。
   * 首次进入页面应为 false（sessionEpoch === 0），避免刷新误清空。
   */
  forceReset?: boolean;
  headerNode?: ReactNode;
  className?: string;
}

/** abr-webchat iframe 嵌入（OIDC return_to → /chat/agent，或直连 /chat/agent） */
export const AbrChatFrame: React.FC<AbrChatFrameProps> = ({
  botId,
  spaceId,
  inferenceMode,
  botName,
  workspaceId,
  forceReset = false,
  headerNode,
  className,
}) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const botNameRef = useRef(botName);
  botNameRef.current = botName;
  const [loading, setLoading] = useState(true);
  const [statusMessage, setStatusMessage] = useState('');
  const setupSeqRef = useRef(0);

  useEffect(() => {
    const seq = ++setupSeqRef.current;
    let disposeBridge: (() => void) | null = null;

    const hideLoadingIfCurrent = () => {
      if (seq === setupSeqRef.current) {
        setLoading(false);
      }
    };

    const setupEmbed = async () => {
      setStatusMessage('');
      setLoading(true);

      const iframe = iframeRef.current;
      if (!iframe) {
        setStatusMessage('页面初始化失败，请刷新重试');
        setLoading(false);
        return;
      }

      if (!botId || !spaceId) {
        setStatusMessage('缺少 bot_id 或 space_id，无法嵌入 abr-webchat');
        setLoading(false);
        return;
      }

      const query: CozeChatQuery = {
        bot_id: botId,
        space_id: spaceId,
        scene: 'debug',
        entry: 'embed',
        ...(inferenceMode && inferenceMode !== 'llm'
          ? { inference_mode: inferenceMode }
          : {}),
        ...(botNameRef.current?.trim()
          ? { bot_name: botNameRef.current.trim() }
          : {}),
        ...(forceReset ? { force_reset: true } : {}),
      };

      const dispose = await startCozeAbrChatEmbed({
        iframe,
        query,
        onHandshakeComplete: hideLoadingIfCurrent,
        onTimeout: hideLoadingIfCurrent,
      });

      if (seq !== setupSeqRef.current) {
        dispose?.();
        return;
      }

      if (!dispose) {
        const env = getCozeEmbedEnv();
        if (isAbrWebchatDirectEmbedEnabled(env)) {
          setStatusMessage(
            env.VITE_ABR_WEBCHAT_URL?.trim()
              ? '无法启动 abr-webchat 直连嵌入，请确认 abr-webchat 可访问'
              : '未配置 VITE_ABR_WEBCHAT_URL，无法嵌入 abr-webchat',
          );
        } else {
          const validation = validateAiQueryOidcConfig(
            buildCozeAiQueryLauncher(env, query).config,
          );
          if (validation.ok === false) {
            setStatusMessage(
              validation.reason === 'missing_idp'
                ? '未配置 VITE_OIDC_IDP_ORIGIN，无法嵌入 abr-webchat'
                : '未配置 VITE_ABR_WEBCHAT_URL，无法嵌入 abr-webchat',
            );
          } else if (!ensureCozeUcSession()) {
            setStatusMessage('未检测到 UC 登录态，请先登录后再开启 ABR 聊天');
          } else {
            setStatusMessage(
              '无法启动 abr-webchat 嵌入，请确认 OIDC IdP 已启动（VITE_OIDC_IDP_ORIGIN）',
            );
          }
        }
        setLoading(false);
        return;
      }

      disposeBridge = dispose;
    };

    void setupEmbed();

    return () => {
      setupSeqRef.current += 1;
      disposeBridge?.();
      const iframe = iframeRef.current;
      if (iframe) {
        iframe.src = 'about:blank';
      }
    };
  }, [botId, spaceId, inferenceMode, workspaceId, forceReset]);

  if (statusMessage) {
    return (
      <div className={classNames(styles.root, className)}>
        {headerNode}
        <div className={styles.status}>{statusMessage}</div>
      </div>
    );
  }

  return (
    <div className={classNames(styles.root, className)}>
      {headerNode}
      <div className={styles.iframeWrapper}>
        <iframe
          ref={iframeRef}
          className={styles.iframe}
          title="abr-webchat"
          allow="clipboard-read; clipboard-write"
        />
        {loading ? (
          <div className={styles.loadingMask}>
            <Spin spinning />
          </div>
        ) : null}
      </div>
    </div>
  );
};

/** @deprecated 使用 AbrChatFrame；保留别名以兼容命名 */
export const AbrChageFrame = AbrChatFrame;
